import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { IContact, IUserInfo } from '../../../shared/models/contact';
import { IDepartment } from '../../../shared/models/department';
import { ContactInfoPopupComponent } from '../../components/contact-info-popup/contact-info-popup.component';
import { ContactsService } from '../../../shared/services/contact.service';
import { UserDataService } from '../../../shared/services/userData.service';
import { CardsListSkeletonComponent } from '../../components/cards-list-skeleton/cards-list-skeleton.component';
import { DepartmentsService } from '../../../shared/services/department.service';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PlanningTeamDetailsComponent } from './planning-team-details/planning-team-details.component';
import { EEopTemplates, ECorePlanningTeamType, ESectionTypes, getSectionNameFromRoute } from '../../../shared/models/eop_types.enum';
import { NoEmojiDirective } from '../../../shared/helpers/noEmoji.directive';
import { EopPlanSlug, getPlanSlugFromUrl } from '../../eop-config/eop-page-config';

const ROUTE_TO_TEAM_TYPE: Record<string, ECorePlanningTeamType> = {
  'school-leadership': ECorePlanningTeamType['School Leadership'],
  'after-hours-contact': ECorePlanningTeamType['After-Hours Contacts'],
  'administrator-names-and-positions': ECorePlanningTeamType['Administrator Names and Positions'],
  'school-safety-committee': ECorePlanningTeamType['School Safety Committee'],
  'district-emergency-contact-information': ECorePlanningTeamType['District Emergency Contact Information'],
  'community-emergency-contact-information': ECorePlanningTeamType['Community Emergency Contact Information'],
  'campus-emergency-response-team-contact-list': ECorePlanningTeamType['Campus Emergency Response Team Contact List']
};

const PLAN_TO_TEMPLATE: Record<EopPlanSlug, EEopTemplates> = {
  'al': EEopTemplates['AL EOP'],
  'ms': EEopTemplates['MS EOP'],
  'ga': EEopTemplates['GA EOP']
};

@Component({
  selector: 'core-planning-team',
  standalone: true,
  imports: [
    LucideAngularModule,
    CommonModule,
    FormsModule,
    PrvNextButtonsComponent,
    ContactInfoPopupComponent,
    CardsListSkeletonComponent,
    EopAssistantComponent,
    PopupMessageComponent,
    IntroductionComponent,
    SpinnerComponent,
    PlanningTeamDetailsComponent,
    NoEmojiDirective
  ],
  templateUrl: './core-planning-team.component.html',
  styleUrl: './core-planning-team.component.css'
})
export class CorePlanningTeamComponent implements OnInit {

  // When embedded inside another page (e.g. via a doc toggle), the caller
  // supplies the team type / title and we drop the full-page chrome.
  @Input() embedded: boolean = false;
  @Input() teamTypeOverride?: ECorePlanningTeamType | number;
  @Input() titleOverride?: string;

  sectionType: ESectionTypes = ESectionTypes['School Leadership'];
  teamType: ECorePlanningTeamType = ECorePlanningTeamType['School Leadership'];
  template: EEopTemplates = EEopTemplates['GA EOP'];
  displayTitle: string = 'School Leadership';

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  searchQuery: string = '';
  loadingContacts: boolean = true;
  contactInfo: IContact | null = null;
  selectedDepartmentId: string | null = null;
  messageModal: string | null = null;
  popupMessage: string = '';
  fullContactsList: IContact[] = [];
  contactsList: IContact[] = [];
  departmentsList: IDepartment[] = [];
  selectedContacts: IContact[] = [];
  allPlanningTeam: any[] = [];

  // Several requests run in parallel on this page (contacts, positions, assignments).
  // Count them so the spinner stays up until the last one settles, instead of the first
  // response hiding it while the others are still in flight.
  private pendingRequests: number = 0;

  get loading(): boolean {
    return this.pendingRequests > 0;
  }

  private startLoading(): void {
    this.pendingRequests++;
  }

  private stopLoading(): void {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
  }
  showPlanningTeamModal: boolean = false;

  constructor(
    private router: Router,
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private departmentsService: DepartmentsService
  ) { }

  ngOnInit(): void {
    this.applyRouteContext();
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadContacts();
    this.loadDepartments();
  }

  private applyRouteContext(): void {
    const url = this.router.url.replace(/\/+$/, '');
    const segments = url.split('/');
    const lastSegment = segments[segments.length - 1] ?? '';

    this.template = PLAN_TO_TEMPLATE[getPlanSlugFromUrl(this.router.url)];

    // Embedded usage: caller passes the team type / title directly, so skip
    // the route-segment lookup (the last segment is the host page's route).
    if (this.teamTypeOverride != null) {
      this.teamType = this.teamTypeOverride as ECorePlanningTeamType;
      if (this.titleOverride) this.displayTitle = this.titleOverride;
      return;
    }

    this.teamType = ROUTE_TO_TEAM_TYPE[lastSegment] ?? ECorePlanningTeamType['School Leadership'];

    const sectionName = getSectionNameFromRoute(lastSegment);
    if (sectionName) {
      this.displayTitle = sectionName;
      this.sectionType = ESectionTypes[sectionName as keyof typeof ESectionTypes];
    }
  }

  loadBuildingAllPlanningTeam(): void {
    if (this.currentBuildingId) {
      this.startLoading();
      this.contactsService.getBuildingPlanningTeam(this.currentBuildingId, this.template, this.teamType).subscribe({
        next: (response) => {
          this.stopLoading();
          if (!response.has_error) {
            this.allPlanningTeam = response.data || [];
            if (this.contactsList.length > 0) {
              this.updateSelectedContacts(Number(this.selectedDepartmentId));
            }
          }
        },
        error: (error: any) => {
          this.stopLoading();
          console.error('Error fetching all planning team:', error.message);
          this.allPlanningTeam = [];
        }
      });
    }
  }

  // load all Contacts and Contacts of a department
  loadContacts(): void {
    this.startLoading();
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loadingContacts = false;
        this.stopLoading();
        this.fullContactsList = response.data || [];
        // Check if user is an nside.io user
        if (this.currentUser?.email?.toLowerCase().endsWith('@nside.io')) {
          this.contactsList = [...this.fullContactsList];
        } else {
          this.contactsList = this.fullContactsList.filter(
            (c: any) => !c.email?.toLowerCase().endsWith('@nside.io')
          );
        }
        if (this.contactsList.length > 0) {
          this.contactsList.sort((a, b) => a.name.localeCompare(b.name));
        }
        this.loadBuildingAllPlanningTeam();
      },
      error: (error: any) => {
        this.stopLoading();
        this.loadingContacts = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  }

  // Load Departments (scoped by team type)
  loadDepartments(): void {
    this.startLoading();
    this.departmentsService.getDepartments(this.template, undefined, String(this.teamType)).subscribe({
      next: (response) => {
        this.stopLoading();
        this.departmentsList = response.data ? response.data : [];
        this.selectedDepartmentId = this.departmentsList.length > 0 ? String(this.departmentsList[0].id) : null;
      },
      error: (error: any) => {
        this.stopLoading();
        console.error('Error fetching departments:', error.message);
      }
    });
  }

  filterContacts(): void {
    const query = this.searchQuery.toLowerCase().trim();

    this.contactsList = this.fullContactsList.filter((item) => {
      return (
        item.name.toLowerCase().includes(query) ||
        item.Title?.toLowerCase().includes(query) ||
        item.workphone?.toLowerCase().includes(query) ||
        item.email?.toLowerCase().includes(query)
      );
    });
  }

  isContactSelected(contact: IContact): boolean {
    return this.selectedContacts.some(selectedContact => selectedContact.id === contact.id);
  }

  onDepartmentChange(event: any): void {
    this.selectedDepartmentId = event.target.value;
    this.updateSelectedContacts(Number(this.selectedDepartmentId));
  }

  updateSelectedContacts(departmentId: number): void {
    const departmentEntries = this.allPlanningTeam.filter(
      entry => Number(entry.collaborative_department) === departmentId
    );

    const contactIds = departmentEntries.map(entry => entry.contact_id);

    this.selectedContacts = this.contactsList
      .filter(contact => contactIds.includes(contact.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  handleOpenModal(modalName: string): void {
    if (modalName === 'planning-team') {
      this.showPlanningTeamModal = true;
    }
  }

  handleCloseModal(modalName: string): void {
    if (modalName === 'planning-team') {
      this.showPlanningTeamModal = false;
    }
  }

  // open contact info pop up
  openInfo(contact: IContact): void {
    this.contactInfo = contact;
  }

  // add contact to the department
  addContactToDepartment(contact: IContact): void {
    if (this.isContactSelected(contact)) {
      alert('This contact has been assigned to this position already.');
    } else {
      this.selectedContacts.push(contact);
    }
  }

  // remove contact from department
  removeContactFromDepartment(selectedContact: IContact): void {
    const index = this.selectedContacts.findIndex(contact => contact.id === selectedContact.id);
    if (index !== -1) {
      this.selectedContacts.splice(index, 1);
    }
  }

  // save new changes in department.
  // The eop backend replaces the full assignment set for the team type, so we send
  // every other department's assignments plus the current department's selection.
  saveSelectedContacts(): void {
    if (this.currentBuildingId && this.selectedDepartmentId) {
      this.startLoading();
      const currentDeptId = Number(this.selectedDepartmentId);

      const otherAssignments = this.allPlanningTeam
        .filter(entry => Number(entry.collaborative_department) !== currentDeptId)
        .map(entry => ({
          collaborative_department: Number(entry.collaborative_department),
          contact_id: entry.contact_id
        }));

      const currentAssignments = this.selectedContacts.map(contact => ({
        collaborative_department: currentDeptId,
        contact_id: contact.id
      }));

      const payload = [...otherAssignments, ...currentAssignments];

      this.contactsService.saveBuildingCorePlanningTeam(
        this.currentBuildingId,
        this.template,
        String(this.teamType),
        payload
      ).subscribe({
        next: (response) => {
          this.stopLoading();
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = `This occurred after saving ${this.displayTitle}.`;
            this.loadBuildingAllPlanningTeam();
          }
        },
        error: (error) => {
          this.stopLoading();
          console.error('Error saving contacts to department:', error.message);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      });
    }
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
}
